import CardZone from "components/CardZone/CardZone";
import {Seats} from "components/Seats/Seats";
import {useTimer} from "contexts/TimerContext";
import {LocalitySelectedType, ZoneType} from "data/types";
import React, {FC, useEffect, useMemo, useState} from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import NcModal from "shared/NcModal/NcModal";
import {
  useDeleteSeatsByLocalityMutation,
  useGetZonesByLocalityQuery,
} from "store/api";

export const Zones: FC<{
  localityId: string;
  eventId: string;
  maxSeats: number;
  endTime: string;
  setIsOpened: React.Dispatch<React.SetStateAction<boolean>>;
  localitiesSelected: LocalitySelectedType[];
  setLocalitiesSelected: React.Dispatch<
    React.SetStateAction<LocalitySelectedType[]>
  >;
}> = ({
  localityId,
  eventId,
  maxSeats,
  endTime,
  setIsOpened,
  localitiesSelected,
  setLocalitiesSelected,
}) => {
  const {data, isLoading} = useGetZonesByLocalityQuery(localityId);
  const [zones, setZones] = useState<ZoneType[]>([]);
  const [alert, setAlert] = useState<string | null>(null);
  const [openModals, setOpenModals] = useState<boolean[]>(
    zones.map(() => false),
  );
  const {formattedTime} = useTimer();
  const [deleteSeats] = useDeleteSeatsByLocalityMutation();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      deleteSeats(localityId);
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleOpenModal = (index: number) => {
    setOpenModals((prev) => {
      const newState = [...prev];
      newState[index] = true;
      return newState;
    });
  };

  const handleCloseModal = (index: number) => {
    setOpenModals((prev) => {
      const newState = [...prev];
      newState[index] = false;
      return newState;
    });
  };

  const handleClose = async () => {
    await deleteSeats(localityId);
    setLocalitiesSelected((prevLocalities) =>
      prevLocalities.map((locality) =>
        locality._id === localityId
          ? {
              ...locality,
              zones: zones.map((zone: any) => ({
                _id: zone._id,
                seats: [],
                numbers: [],
                customers: [],
              })),
            }
          : locality,
      ),
    );
    setIsOpened(false);
  };
  const sumSelectedSeats = localitiesSelected
    .find((loc) => loc._id === localityId)!
    .zones.reduce((acc, item) => acc + item.seats.length, 0);

  const showAlert = () => {
    setAlert(
      "El número de asientos seleccionados no puede superar el límite de asientos permitidos",
    );
    setTimeout(() => setAlert(null), 3000);
  };

  useEffect(() => {
    if (data) {
      console.log(data);
      const newZones = data?.zone.map((zone: any) => ({
        _id: zone._id,
        locality: zone.locality,
        cols: zone.cols,
        rows: zone.rows,
        number: zone.number,
        seat: zone.seat.map((seat: any) => ({
          seat_map: seat.seat_map.map((seatMap: any) => ({
            layout: seatMap.layout,
            letter: seatMap.letter,
            position: seatMap.position,
            seatLabel: seatMap.seat_label,
            start: seatMap.start,
          })),
        })),
      }));
      setZones(newZones);

      localitiesSelected.forEach((locality) => {
        if (locality._id === localityId && locality.zones.length === 0) {
          const nz = newZones.map((zone: any) => ({
            _id: zone._id,
            seats: [],
            numbers: [],
            customers: [],
          }));
          setLocalitiesSelected((prevLocalities) =>
            prevLocalities.map((locality) =>
              locality._id === localityId ? {...locality, zones: nz} : locality,
            ),
          );
        }
      });
    }
  }, [data]);

  useEffect(() => {
    if (zones.length === 1) {
      console.log("solo tiene una zona");

      // setOpenModals(zones.map(() => false));
    }
  }, [zones]);

  const getSeatsLength = useMemo(
    () =>
      (zoneIndex: number): number => {
        const locality = localitiesSelected.find(
          (loc) => loc._id === localityId,
        );
        return locality?.zones?.[zoneIndex]?.seats?.length || 0;
      },
    [localitiesSelected, localityId],
  );
  return (
    <div className="w-full flex flex-col sm:rounded-2xl lg:border border-neutral-200 dark:border-neutral-700 space-y-6 sm:space-y-8 px-0 sm:p-6 xl:p-8">
      <p>
        * Para conservar los asientos seleccionados, asegúrate de guardar los
        cambios.
      </p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        {!isLoading &&
          zones.map((zone: ZoneType, index: number) => (
            <NcModal
              showCloseButton={false}
              closeOnClickOutside={false}
              key={zone._id}
              isOpenProp={openModals[index] || zones.length === 1}
              onCloseModal={() => handleCloseModal(index)}
              renderTrigger={(openModal) => (
                <CardZone
                  key={zone._id}
                  title={`Zona ${index + 1}`}
                  openModal={
                    localitiesSelected.find((loc) => loc._id === localityId)
                      ?.zones[index].seats.length === 0 &&
                    sumSelectedSeats >= maxSeats
                      ? showAlert
                      : () => handleOpenModal(index)
                  }
                  selected={getSeatsLength(index)}
                />
              )}
              renderContent={() => (
                <Seats
                  zone={zone}
                  maxSeats={maxSeats}
                  endTime={endTime}
                  setIsOpen={() => handleCloseModal(index)}
                  localitySelected={localitiesSelected}
                  setLocalitySelected={setLocalitiesSelected}
                  eventId={eventId}
                  setIsOpenedZones={setIsOpened}
                  zonesQuantity={zones.length}
                  handleClose={handleClose}
                />
              )}
              modalTitle={`Seleccionar Asientos ${formattedTime}`}
            />
          ))}
      </div>
      <div className="flex justify-evenly items-center h-full">
        <ButtonSecondary onClick={handleClose}>Cancelar</ButtonSecondary>
        <ButtonPrimary onClick={() => setIsOpened(false)}>
          Guardar
        </ButtonPrimary>
      </div>
      {alert && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-2xl shadow-2xl transition-all duration-300 ease-in-out opacity-90 hover:opacity-100 hover:scale-105 flex items-center gap-4">
          {alert}
        </div>
      )}
    </div>
  );
};
